import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainSidebar from 'components/ui/MainSidebar';
import Icon from 'components/AppIcon';
import FilterToolbar from './components/FilterToolbar';
import OpportunityCard from './components/OpportunityCard';
import OpportunityDetailModal from './components/OpportunityDetailModal';
import InterestModal from './components/InterestModal';
import EmptyState from './components/EmptyState';
import { supabase } from '../../lib/supabase';


const OPPORTUNITIES = [
{
  id: 1,
  name: "NourishHer Foods",
  sector: "Food & Beverage",
  description: "Plant-based nutrition company focused on women's health needs, offering subscription meal kits and supplements designed by female nutritionists.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10dbf8d4b-1766540408813.png",
  imageAlt: "Colorful array of fresh organic vegetables and plant-based foods arranged on white marble surface",
  fundingStage: "Series A",
  minInvestment: 5000,
  projectedROI: 28,
  riskLevel: "Medium",
  isWomenLed: true,
  isWinPick: true,
  isSaved: false,
  fundingProgress: 67,
  raisedAmount: 1340000,
  targetAmount: 2000000,
  location: "Austin, TX",
  highlights: [
  "3x revenue growth in 12 months",
  "Partnership with 50+ corporate wellness programs",
  "Patent-pending nutritional formulation",
  "Featured in Forbes Women 2025"],

  founder: {
    name: "Dr. Amara Johnson",
    title: "CEO & Co-Founder",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15751363f-1763296851612.png",
    avatarAlt: "Professional Black woman with natural hair in white lab coat smiling confidently",
    bio: "Dr. Amara Johnson holds a PhD in Nutritional Science from Stanford and spent 8 years at Nestlé Health Science before founding NourishHer. Her research on women\'s micronutrient deficiencies has been cited in over 40 peer-reviewed journals.",
    linkedin: true,
    achievements: ["Forbes 30 Under 30", "NIH Research Grant", "SXSW Pitch Winner", "500 Startups Alum"]
  },
  financials: [
  { label: "Annual Revenue", value: "$2.4M", change: 210 },
  { label: "Gross Margin", value: "62%", change: 8 },
  { label: "MoM Growth", value: "18%", change: null },
  { label: "Valuation", value: "$12M", change: null }],

  team: [
  { name: "Priya Sharma", role: "CTO", background: "Ex-Google, MIT CS", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17d2a97fc-1772147305559.png", avatarAlt: "South Asian woman with glasses and dark hair in professional business attire" },
  { name: "Marcus Webb", role: "CFO", background: "Goldman Sachs, 12 yrs", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ec4063d1-1763296299750.png", avatarAlt: "Professional Black man in navy suit with confident expression" },
  { name: "Sofia Reyes", role: "CMO", background: "Ex-Glossier, NYU MBA", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d11c76b3-1763294269112.png", avatarAlt: "Latina woman with long dark hair in creative office environment" },
  { name: "James Park", role: "VP Sales", background: "Whole Foods, 8 yrs", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1418365a1-1763293544915.png", avatarAlt: "Asian man in business casual attire with friendly professional smile" }]

},
{
  id: 2,
  name: "ShieldTech Security",
  sector: "Technology",
  description: "AI-powered personal safety platform providing real-time threat detection and emergency response coordination for women in urban environments.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10d709d68-1772868866021.png",
  imageAlt: "Modern smartphone displaying security application interface with blue digital protection graphics",
  fundingStage: "Seed",
  minInvestment: 1000,
  projectedROI: 45,
  riskLevel: "High",
  isWomenLed: true,
  isWinPick: false,
  isSaved: false,
  fundingProgress: 42,
  raisedAmount: 420000,
  targetAmount: 1000000,
  location: "New York, NY",
  highlights: [
  "50,000+ active users in beta",
  "Partnership with 3 major city police departments",
  "AI accuracy rate of 94.7%",
  "TechCrunch Disrupt finalist 2025"],

  founder: {
    name: "Keisha Williams",
    title: "CEO & Founder",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1127ff6dd-1772292646388.png",
    avatarAlt: "Black woman with short natural hair in tech startup office with modern equipment",
    bio: "Former NYPD detective turned tech entrepreneur, Keisha Williams built ShieldTech after identifying critical gaps in personal safety technology. She holds an MBA from Columbia and has 15 years of law enforcement experience.",
    linkedin: true,
    achievements: ["NYPD Detective", "Columbia MBA", "TechCrunch Disrupt Finalist", "Y Combinator W24"]
  },
  financials: [
  { label: "ARR", value: "$480K", change: 340 },
  { label: "Gross Margin", value: "78%", change: 12 },
  { label: "CAC", value: "$12", change: null },
  { label: "Valuation", value: "$5M", change: null }],

  team: [
  { name: "Alex Chen", role: "CTO", background: "Ex-Palantir, MIT AI Lab", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15d805f55-1763295574780.png", avatarAlt: "Asian man with glasses in modern tech office surrounded by multiple monitors" },
  { name: "Destiny Moore", role: "Head of Product", background: "Ex-Uber Safety, Stanford", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18e34d4c5-1772868864226.png", avatarAlt: "Black woman with braids in product design meeting with sticky notes on wall" }]

},
{
  id: 3,
  name: "Verdant Capital REIT",
  sector: "Real Estate",
  description: "Sustainable real estate investment trust focusing on eco-friendly residential developments in high-growth Sun Belt markets with strong rental yields.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11b742a1f-1765104623062.png",
  imageAlt: "Modern sustainable apartment complex with green rooftop gardens and solar panels in sunny urban setting",
  fundingStage: "Growth",
  minInvestment: 25000,
  projectedROI: 14,
  riskLevel: "Low",
  isWomenLed: false,
  isWinPick: false,
  isSaved: true,
  fundingProgress: 88,
  raisedAmount: 8800000,
  targetAmount: 10000000,
  location: "Phoenix, AZ",
  highlights: [
  "8 completed properties, 100% occupancy",
  "LEED Platinum certified developments",
  "Average 14.2% annual returns since 2020",
  "SEC Regulation A+ qualified offering"],

  founder: {
    name: "Robert Castellano",
    title: "Managing Partner",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e1dca615-1772868862526.png",
    avatarAlt: "Hispanic man in business suit standing in front of modern real estate development",
    bio: "Robert Castellano brings 20 years of real estate development experience, having completed over $500M in sustainable residential projects across the Southwest. He holds a CCIM designation and is a LEED Accredited Professional.",
    linkedin: true,
    achievements: ["CCIM Designation", "LEED AP", "$500M+ Developed", "ULI Member"]
  },
  financials: [
  { label: "Total Assets", value: "$42M", change: 28 },
  { label: "Dividend Yield", value: "8.4%", change: 2 },
  { label: "Occupancy Rate", value: "100%", change: null },
  { label: "NAV per Share", value: "$24.80", change: null }],

  team: [
  { name: "Linda Park", role: "CFO", background: "Ex-Blackstone, CPA", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f91326c4-1763294357707.png", avatarAlt: "Asian woman in professional business attire reviewing financial documents at desk" },
  { name: "Tom Bradley", role: "Head of Acquisitions", background: "20 yrs RE Development", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce4fbee1-1772224357091.png", avatarAlt: "White man in hard hat and business casual at construction site inspection" }]

},
{
  id: 4,
  name: "MindBridge Wellness",
  sector: "Healthcare",
  description: "Digital mental health platform offering AI-guided therapy sessions, peer support communities, and licensed therapist matching specifically designed for working women.",
  image: "https://images.unsplash.com/photo-1714976694261-f34b07bac98a",
  imageAlt: "Woman sitting peacefully in bright modern therapy room with plants and calming decor",
  fundingStage: "Series A",
  minInvestment: 2500,
  projectedROI: 35,
  riskLevel: "Medium",
  isWomenLed: true,
  isWinPick: true,
  isSaved: false,
  fundingProgress: 55,
  raisedAmount: 1375000,
  targetAmount: 2500000,
  location: "San Francisco, CA",
  highlights: [
  "92% user satisfaction rate",
  "Partnerships with 200+ employers",
  "HIPAA compliant platform",
  "APA endorsed methodology"],

  founder: {
    name: "Dr. Maya Patel",
    title: "CEO & Founder",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bf19b1fa-1772136954530.png",
    avatarAlt: "South Asian woman with warm smile in professional medical office setting",
    bio: "Dr. Maya Patel is a licensed clinical psychologist with a PhD from UCSF and 12 years of practice. She founded MindBridge after recognizing that traditional therapy models fail working women due to scheduling and cost barriers.",
    linkedin: true,
    achievements: ["UCSF PhD Psychology", "APA Fellow", "TIME100 Health 2024", "a16z Bio Fund Portfolio"]
  },
  financials: [
  { label: "ARR", value: "$3.2M", change: 180 },
  { label: "Gross Margin", value: "71%", change: 5 },
  { label: "NPS Score", value: "72", change: null },
  { label: "Valuation", value: "$18M", change: null }],

  team: [
  { name: "Sarah Kim", role: "CTO", background: "Ex-Headspace, Stanford", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1600ec31c-1763301363205.png", avatarAlt: "Korean American woman in casual tech attire working on laptop in modern office" },
  { name: "David Osei", role: "Head of Clinical", background: "Harvard Med, 15 yrs", avatar: "https://images.unsplash.com/photo-1666887360313-43de76a966da", avatarAlt: "African man in white medical coat with stethoscope in clinical setting" }]

},
{
  id: 5,
  name: "EduVenture Academy",
  sector: "Education",
  description: "EdTech platform providing vocational training and micro-credentials for women re-entering the workforce, with employer placement guarantees.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12a040020-1766866891228.png",
  imageAlt: "Diverse group of women students collaborating on laptops in bright modern learning space",
  fundingStage: "Seed",
  minInvestment: 500,
  projectedROI: 22,
  riskLevel: "Medium",
  isWomenLed: true,
  isWinPick: false,
  isSaved: false,
  fundingProgress: 31,
  raisedAmount: 155000,
  targetAmount: 500000,
  location: "Chicago, IL",
  highlights: [
  "87% job placement rate within 90 days",
  "Partnerships with 150+ hiring companies",
  "Income Share Agreement model available",
  "DOL approved training provider"],

  founder: {
    name: "Tanya Brooks",
    title: "CEO & Co-Founder",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bf8aa48c-1772094671203.png",
    avatarAlt: "Black woman with natural hair in educational setting surrounded by books and technology",
    bio: "Tanya Brooks spent 10 years in workforce development at the Chicago Department of Family Services before founding EduVenture. She holds an EdD from Northwestern and is passionate about economic mobility for women.",
    linkedin: true,
    achievements: ["Northwestern EdD", "DOL Advisory Board", "Chicago Innovation Award", "Echoing Green Fellow"]
  },
  financials: [
  { label: "Revenue", value: "$380K", change: 95 },
  { label: "Gross Margin", value: "58%", change: 7 },
  { label: "Avg. Student ROI", value: "340%", change: null },
  { label: "Valuation", value: "$3M", change: null }],

  team: [
  { name: "Carlos Rivera", role: "CTO", background: "Ex-Coursera, UChicago", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d251ccb4-1772868865917.png", avatarAlt: "Latino man in casual attire working on educational technology platform" },
  { name: "Jasmine Lee", role: "Head of Partnerships", background: "LinkedIn, 8 yrs", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14e9d9fce-1772868868844.png", avatarAlt: "Asian woman in business professional attire at corporate networking event" }]

},
{
  id: 6,
  name: "GreenGrid Energy",
  sector: "Energy",
  description: "Community solar platform enabling residential customers to subscribe to local solar farms, reducing energy bills by 20-30% with no installation required.",
  image: "https://images.unsplash.com/photo-1644349057008-19f93297bc0c",
  imageAlt: "Large solar panel array in green field with blue sky and white clouds in background",
  fundingStage: "Series B",
  minInvestment: 10000,
  projectedROI: 18,
  riskLevel: "Low",
  isWomenLed: false,
  isWinPick: false,
  isSaved: false,
  fundingProgress: 74,
  raisedAmount: 7400000,
  targetAmount: 10000000,
  location: "Denver, CO",
  highlights: [
  "15,000+ active subscribers",
  "Operating in 12 states",
  "Utility partnerships with 8 major providers",
  "ITC tax credit eligible investment"],

  founder: {
    name: "Elena Vasquez",
    title: "CEO & Founder",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1261446ff-1772868871526.png",
    avatarAlt: "Latina woman in hard hat at solar energy installation site with panels in background",
    bio: "Elena Vasquez is a former energy policy advisor who spent 8 years at the Department of Energy before founding GreenGrid. She holds an MS in Environmental Engineering from MIT and an MBA from Wharton.",
    linkedin: true,
    achievements: ["MIT MS Engineering", "Wharton MBA", "DOE Policy Advisor", "CleanTech 100"]
  },
  financials: [
  { label: "ARR", value: "$8.4M", change: 67 },
  { label: "Gross Margin", value: "44%", change: 3 },
  { label: "Subscriber Growth", value: "+340%", change: null },
  { label: "Valuation", value: "$45M", change: null }],

  team: [
  { name: "Wei Zhang", role: "CTO", background: "Ex-Tesla Energy, Caltech", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ed67f3ee-1772868876486.png", avatarAlt: "Asian man in engineering attire reviewing technical solar installation diagrams" },
  { name: "Nadia Okonkwo", role: "CFO", background: "JP Morgan, 14 yrs", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1eac4bd5f-1772658508857.png", avatarAlt: "Nigerian woman in professional business suit at financial services office" }]

},
{
  id: 7,
  name: "LuxeFinance Pro",
  sector: "Finance",
  description: "Robo-advisory platform with human oversight designed specifically for high-net-worth women, offering personalized wealth management and estate planning services.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12f035801-1766489709151.png",
  imageAlt: "Professional woman reviewing financial charts and investment portfolio on tablet in modern office",
  fundingStage: "Pre-IPO",
  minInvestment: 50000,
  projectedROI: 12,
  riskLevel: "Low",
  isWomenLed: true,
  isWinPick: false,
  isSaved: true,
  fundingProgress: 92,
  raisedAmount: 18400000,
  targetAmount: 20000000,
  location: "New York, NY",
  highlights: [
  "$2.1B AUM in 3 years",
  "SEC registered investment advisor",
  "Average client portfolio: $850K",
  "IPO planned Q3 2026"],

  founder: {
    name: "Victoria Chen",
    title: "CEO & Founder",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_197743571-1772868871888.png",
    avatarAlt: "Chinese American woman in elegant business attire at luxury financial services office",
    bio: "Victoria Chen spent 18 years at Goldman Sachs as a Managing Director before founding LuxeFinance. She holds an MBA from Harvard Business School and is a CFA charterholder with expertise in alternative investments.",
    linkedin: true,
    achievements: ["Harvard MBA", "CFA Charterholder", "Goldman Sachs MD", "Barron\'s Top 100 Advisors"]
  },
  financials: [
  { label: "AUM", value: "$2.1B", change: 145 },
  { label: "Revenue", value: "$21M", change: 89 },
  { label: "Client Retention", value: "97%", change: null },
  { label: "Valuation", value: "$180M", change: null }],

  team: [
  { name: "Michael Torres", role: "CTO", background: "Ex-Bloomberg, CMU", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_12273cedd-1772868869437.png", avatarAlt: "Hispanic man in business attire at fintech company with financial data screens" },
  { name: "Aisha Mensah", role: "Chief Investment Officer", background: "BlackRock, 16 yrs", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_157e60058-1772868863243.png", avatarAlt: "African woman in professional investment banking attire reviewing market analysis" }]

},
{
  id: 8,
  name: "StyleCircle Resale",
  sector: "Consumer Goods",
  description: "Peer-to-peer luxury fashion resale platform with AI-powered authentication, targeting the $50B secondhand luxury market with a focus on women sellers.",
  image: "https://images.unsplash.com/photo-1597358371607-5987dd7da3d6",
  imageAlt: "Curated collection of luxury designer handbags and accessories displayed on white background",
  fundingStage: "Series A",
  minInvestment: 2000,
  projectedROI: 32,
  riskLevel: "Medium",
  isWomenLed: true,
  isWinPick: false,
  isSaved: false,
  fundingProgress: 48,
  raisedAmount: 960000,
  targetAmount: 2000000,
  location: "Los Angeles, CA",
  highlights: [
  "200,000+ registered sellers",
  "AI authentication accuracy 99.2%",
  "Average GMV per seller: $4,200/year",
  "Featured in Vogue Business 2025"],

  founder: {
    name: "Isabelle Fontaine",
    title: "CEO & Co-Founder",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_196439224-1772868875317.png",
    avatarAlt: "French American woman with elegant style in fashion industry office with clothing racks",
    bio: "Isabelle Fontaine brings 12 years of luxury retail experience from LVMH and Farfetch. She founded StyleCircle after identifying that women were leaving significant value on the table with their luxury wardrobes.",
    linkedin: true,
    achievements: ["LVMH Executive", "Farfetch VP", "CFDA Member", "Sequoia Scout"]
  },
  financials: [
  { label: "GMV", value: "$28M", change: 220 },
  { label: "Take Rate", value: "18%", change: null },
  { label: "Revenue", value: "$5M", change: 195 },
  { label: "Valuation", value: "$22M", change: null }],

  team: [
  { name: "Ryan Nakamura", role: "CTO", background: "Ex-eBay, Stanford AI", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e9eec9b7-1772868863539.png", avatarAlt: "Japanese American man in casual tech attire working on AI authentication system" },
  { name: "Camille Dubois", role: "Head of Authentication", background: "Christie's, 10 yrs", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1621cc1b8-1772868863153.png", avatarAlt: "French woman with sophisticated style examining luxury item with authentication tools" }]

}];


const DEFAULT_FILTERS = {
  sector: 'All Sectors',
  riskLevel: 'All Risk',
  minInvestment: 'Any Amount',
  roiPotential: 'Any ROI',
  womenLed: false
};

function matchesMinInvestment(value, filter) {
  if (filter === 'Any Amount') return true;
  if (filter === 'Under $1K') return value < 1000;
  if (filter === '$1K - $5K') return value >= 1000 && value <= 5000;
  if (filter === '$5K - $25K') return value > 5000 && value <= 25000;
  if (filter === '$25K+') return value > 25000;
  return true;
}

function matchesROI(value, filter) {
  if (filter === 'Any ROI') return true;
  const threshold = parseInt(filter);
  return value >= threshold;
}

export default function InvestmentOpportunities() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sortBy, setSortBy] = useState('Relevance');
  const [viewMode, setViewMode] = useState('grid');
  const [opportunities, setOpportunities] = useState(OPPORTUNITIES);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [interestOpportunity, setInterestOpportunity] = useState(null);
  const [toast, setToast] = useState(null);

  // Fetch approved showcase applications
  useEffect(() => {
    const fetchApproved = async () => {
      try {
        const { data, error } = await supabase?.from('showcase_applications')?.select('*')?.eq('status', 'approved')?.order('reviewed_at', { ascending: false });
        if (error || !data?.length) return;
        const mapped = data?.map((app) => ({
          id: `showcase-${app?.id}`,
          name: app?.business_name,
          sector: app?.sector,
          description: app?.description || '',
          image: app?.profile_photo_url || 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2',
          imageAlt: `${app?.founder_name} - ${app?.business_name} founder`,
          fundingStage: app?.funding_stage,
          minInvestment: app?.min_investment || 0,
          projectedROI: app?.projected_roi || 0,
          riskLevel: 'Medium',
          isWomenLed: app?.is_women_led,
          isWinPick: true,
          isFeaturedFounder: true,
          isSaved: false,
          fundingProgress: 0,
          raisedAmount: 0,
          targetAmount: app?.funding_goal || 0,
          location: app?.location || '',
          highlights: [],
          founder: {
            name: app?.founder_name,
            title: 'Founder',
            avatar: app?.profile_photo_url || '',
            avatarAlt: `${app?.founder_name} profile photo`,
            bio: app?.description || '',
            linkedin: !!app?.linkedin_url,
            achievements: []
          },
          financials: [
          { label: 'Min. Investment', value: app?.min_investment ? `$${Number(app?.min_investment)?.toLocaleString()}` : 'TBD', change: null },
          { label: 'Projected ROI', value: app?.projected_roi ? `${app?.projected_roi}%` : 'TBD', change: null },
          { label: 'Funding Goal', value: app?.funding_goal ? `$${Number(app?.funding_goal)?.toLocaleString()}` : 'TBD', change: null },
          { label: 'Timeline', value: app?.investment_timeline || 'TBD', change: null }],

          team: []
        }));
        setOpportunities([...mapped, ...OPPORTUNITIES]);
      } catch (err) {

        // Silently fail — static data still shows
      }};
    fetchApproved();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = (id) => {
    setOpportunities((prev) => prev?.map((o) => o?.id === id ? { ...o, isSaved: !o?.isSaved } : o));
    const opp = opportunities?.find((o) => o?.id === id);
    showToast(opp?.isSaved ? 'Removed from saved' : 'Saved to your list');
    if (selectedOpportunity?.id === id) {
      setSelectedOpportunity((prev) => ({ ...prev, isSaved: !prev?.isSaved }));
    }
  };

  const handleExpressInterest = (opportunity) => {
    setSelectedOpportunity(null);
    setInterestOpportunity(opportunity);
  };

  const handleInterestSubmit = () => {
    showToast('Interest submitted successfully! The founder will be in touch.');
  };

  const filteredAndSorted = useMemo(() => {
    let result = opportunities?.filter((o) => {
      if (filters?.sector !== 'All Sectors' && o?.sector !== filters?.sector) return false;
      if (filters?.riskLevel !== 'All Risk' && o?.riskLevel !== filters?.riskLevel) return false;
      if (!matchesMinInvestment(o?.minInvestment, filters?.minInvestment)) return false;
      if (!matchesROI(o?.projectedROI, filters?.roiPotential)) return false;
      if (filters?.womenLed && !o?.isWomenLed) return false;
      return true;
    });

    if (sortBy === 'ROI: High to Low') result = [...result]?.sort((a, b) => b?.projectedROI - a?.projectedROI);else
    if (sortBy === 'Min Investment: Low to High') result = [...result]?.sort((a, b) => a?.minInvestment - b?.minInvestment);else
    if (sortBy === 'Funding Stage') result = [...result]?.sort((a, b) => a?.fundingStage?.localeCompare(b?.fundingStage));else
    result = [...result]?.sort((a, b) => (b?.isWinPick ? 1 : 0) - (a?.isWinPick ? 1 : 0));

    return result;
  }, [opportunities, filters, sortBy]);

  const sidebarWidth = sidebarCollapsed ? 64 : 240;

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-surface)' }}>
      <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
      {/* Main content */}
      <div
        className={`transition-all duration-250 min-h-screen flex flex-col pb-20 lg:pb-0 ${sidebarCollapsed ? 'lg:ml-[64px]' : 'lg:ml-[240px]'}`}>
        
        {/* Page Header */}
        <div className="px-4 md:px-6 lg:px-8 pt-6 pb-4" style={{ background: 'var(--color-primary)' }}>
          <div className="flex items-start justify-between gap-4 pl-10 lg:pl-0">
            <div>
              <h1 className="font-heading font-bold text-white text-2xl md:text-3xl">Investment Opportunities</h1>
              <p className="font-caption text-sm mt-1" style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'var(--font-caption)' }}>
                Vetted opportunities curated by the WIN network
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(133,113,77,0.15)', border: '1px solid rgba(133,113,77,0.3)' }}>
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="font-caption text-xs text-white" style={{ fontFamily: 'var(--font-caption)' }}>Live Opportunities</span>
              </div>
            </div>
          </div>

          {/* Stats strip */}
          <div className="flex gap-4 md:gap-8 mt-4 pl-10 lg:pl-0 overflow-x-auto pb-1">
            {[
            { label: 'Total Opportunities', value: OPPORTUNITIES?.length },
            { label: 'Women-Led', value: OPPORTUNITIES?.filter((o) => o?.isWomenLed)?.length },
            { label: 'WIN Picks', value: OPPORTUNITIES?.filter((o) => o?.isWinPick)?.length },
            { label: 'Avg. ROI', value: `${Math.round(OPPORTUNITIES?.reduce((s, o) => s + o?.projectedROI, 0) / OPPORTUNITIES?.length)}%` }]?.
            map((stat) =>
            <div key={stat?.label} className="flex-shrink-0">
                <p className="font-data font-bold text-lg md:text-xl" style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-data)' }}>{stat?.value}</p>
                <p className="font-caption text-xs" style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'var(--font-caption)' }}>{stat?.label}</p>
              </div>
            )}
          </div>
        </div>

        {/* Filter Toolbar */}
        <FilterToolbar
          filters={filters}
          onFilterChange={setFilters}
          sortBy={sortBy}
          onSortChange={setSortBy}
          totalCount={filteredAndSorted?.length}
          viewMode={viewMode}
          onViewModeChange={setViewMode} />
        

        {/* Opportunities Grid */}
        <div className="flex-1 px-4 md:px-6 lg:px-8 py-6 pb-24 lg:pb-6">
          {filteredAndSorted?.length === 0 ?
          <EmptyState onClearFilters={() => setFilters(DEFAULT_FILTERS)} /> :

          <div className={
          viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5' : 'flex flex-col gap-3'
          }>
              {filteredAndSorted?.map((opportunity, index) =>
            <div key={opportunity?.id} className={`card-enter card-enter-${Math.min(index + 1, 8)}`}>
              <OpportunityCard
                key={opportunity?.id}
                opportunity={opportunity}
                viewMode={viewMode}
                onSave={handleSave}
                onExpressInterest={handleExpressInterest}
                onClick={setSelectedOpportunity} />
            </div>

            )}
            </div>
          }
        </div>
      </div>
      {/* Detail Modal */}
      {selectedOpportunity &&
      <OpportunityDetailModal
        opportunity={selectedOpportunity}
        onClose={() => setSelectedOpportunity(null)}
        onExpressInterest={handleExpressInterest}
        onSave={handleSave} />

      }
      {/* Interest Modal */}
      {interestOpportunity &&
      <InterestModal
        opportunity={interestOpportunity}
        onClose={() => setInterestOpportunity(null)}
        onSubmit={handleInterestSubmit} />

      }
      {/* Toast */}
      {toast &&
      <div
        className="fixed bottom-24 lg:bottom-6 right-4 md:right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl animate-fade-in-up"
        style={{ background: toast?.type === 'success' ? 'var(--color-primary)' : 'var(--color-error)', color: 'white', maxWidth: '320px' }}>
        
          <Icon name={toast?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} color="white" />
          <span className="font-caption text-sm" style={{ fontFamily: 'var(--font-caption)' }}>{toast?.message}</span>
        </div>
      }
    </div>);

}